<template>
  <div class="traveler-list-page">
    <div v-if="selectionMode" class="selection-header">
      <button type="button" class="selection-back" @click="$router.back()">‹ 返回</button>
      <div class="selection-title">选择出行人</div>
      <div class="selection-placeholder"></div>
    </div>

    <div v-if="selectionMode" class="selection-tip">
      <div>请选择本次出行人员</div>
      <div class="selection-count">需选 {{ requiredCount }} 位，已选 {{ selectedIds.length }} 位</div>
    </div>

    <!-- 出行人列表 -->
    <div class="traveler-list-container">
      <div v-if="travelers.length > 0" class="travelers-list">
        <div
          v-for="traveler in travelers"
          :key="traveler.id"
          class="traveler-item"
          :class="{ 'traveler-item--selectable': selectionMode, selected: isSelected(traveler.id) }"
          @click="selectionMode && toggleSelection(traveler.id)"
        >
          <div v-if="selectionMode" class="traveler-check" aria-hidden="true">
            <span v-if="isSelected(traveler.id)">✓</span>
          </div>
          <div class="traveler-info">
            <div class="traveler-name">{{ traveler.name }}</div>
            <div class="traveler-detail">{{ traveler.idCard }}</div>
          </div>
          <div class="traveler-actions">
            <button class="action-icon edit-btn" aria-label="编辑出行人" @click.stop="editTraveler(traveler)">✏️</button>
            <button class="action-icon delete-btn" aria-label="删除出行人" @click.stop="deleteTraveler(traveler.id)">🗑️</button>
          </div>
        </div>
      </div>
      <empty-state
        v-else
        title="暂无出行人"
        description="请添加出行人信息"
        emoji="✈️"
        :show-button="true"
        button-text="添加出行人"
        @click="openAddForm"
      />
    </div>

    <!-- 添加/编辑表单 -->
    <div v-if="showForm" class="modal-overlay" @click.self="showForm = false">
      <div class="form-container">
        <div class="form-header">
          <div class="form-title">{{ editingId ? '编辑出行人' : '添加出行人' }}</div>
          <div class="form-close" @click="showForm = false">✕</div>
        </div>

        <div class="form-content">
          <div class="form-group">
            <label class="form-label">姓名</label>
            <input v-model="formData.name" type="text" class="form-input" placeholder="请输入姓名" />
          </div>

          <div class="form-group">
            <label class="form-label">证件类型</label>
            <select v-model="formData.idType" class="form-input">
              <option v-for="(t, idx) in idTypes" :key="t.id" :value="idx">{{ t.name }}</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">证件号码</label>
            <input v-model="formData.idCard" type="text" class="form-input" placeholder="请输入证件号码" />
          </div>

          <div class="form-group">
            <label class="form-label">手机号</label>
            <input v-model="formData.phone" type="tel" class="form-input" placeholder="请输入手机号" />
          </div>

          <div class="form-actions">
            <button class="form-btn cancel-btn" @click="showForm = false">取消</button>
            <button class="form-btn submit-btn" @click="handleSubmit">{{ editingId ? '更新' : '添加' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部添加按钮 -->
    <div class="traveler-footer" :class="{ 'traveler-footer--select': selectionMode }">
      <button class="add-btn add-btn--secondary" @click="openAddForm">+ 添加出行人</button>
      <button
        v-if="selectionMode"
        class="confirm-selection-btn"
        :disabled="selectedIds.length !== requiredCount"
        @click="confirmSelection"
      >
        {{ selectedIds.length === requiredCount ? '确认选择并返回订单' : `还需选择 ${requiredCount - selectedIds.length} 位` }}
      </button>
    </div>
  </div>
</template>

<script>
import { getTravelerList, addTraveler, updateTraveler, deleteTraveler as deleteTravelerApi } from '@/api/user'
import EmptyState from '@/components/empty-state/empty-state.vue'
import { validateRequired, validateIdCard, validatePhone } from '@/utils/validate'

const TRAVELER_SELECTION_KEY = 'sweetour_order_traveler_selection'

export default {
  name: 'UserTraveler',
  components: {
    EmptyState
  },
  data() {
    return {
      travelers: [],
      selectionMode: false,
      requiredCount: 0,
      selectedIds: [],
      showForm: false,
      editingId: null,
      idTypes: [
        { id: 'id_card', name: '身份证' },
        { id: 'passport', name: '护照' },
        { id: 'other', name: '其他' }
      ],
      formData: {
        name: '',
        idType: 0,
        idCard: '',
        phone: ''
      },
      loading: false
    }
  },
  mounted() {
    this.selectionMode = this.$route.query.mode === 'select'
    this.requiredCount = this.selectionMode ? Math.max(1, Number(this.$route.query.requiredCount) || 1) : 0
    this.selectedIds = this.selectionMode
      ? String(this.$route.query.selectedIds || '').split(',').map(Number).filter(id => id > 0).slice(0, this.requiredCount)
      : []
    this.loadTravelers()
  },
  methods: {
    async loadTravelers() {
      try {
        const res = await getTravelerList()
        this.travelers = res.data || []
      } catch (error) {
        console.error('加载出行人列表失败:', error)
        this.$alert('加载失败，请重试')
      }
    },
    openAddForm() {
      this.resetForm()
      this.showForm = true
    },
    isSelected(id) {
      return this.selectedIds.includes(Number(id))
    },
    toggleSelection(id) {
      const travelerId = Number(id)
      const index = this.selectedIds.indexOf(travelerId)
      if (index >= 0) {
        this.selectedIds.splice(index, 1)
        return
      }
      if (this.selectedIds.length >= this.requiredCount) {
        this.$alert(`本订单只需选择 ${this.requiredCount} 位出行人`)
        return
      }
      this.selectedIds.push(travelerId)
    },
    confirmSelection() {
      if (this.selectedIds.length !== this.requiredCount) {
        this.$alert(`请选择 ${this.requiredCount} 位出行人`)
        return
      }
      sessionStorage.setItem(TRAVELER_SELECTION_KEY, JSON.stringify(this.selectedIds))
      this.$router.back()
    },
    editTraveler(traveler) {
      this.editingId = traveler.id
      this.formData = {
        name: traveler.name,
        idType: 0,
        idCard: traveler.idCard,
        phone: traveler.phone
      }
      this.showForm = true
    },
    async deleteTraveler(id) {
      const ok = await this.$confirm('确定要删除该出行人吗？')
      if (ok) {
        try {
          await deleteTravelerApi(id)
          this.$alert('删除成功')
          this.loadTravelers()
        } catch (error) {
          console.error('删除失败:', error)
          this.$alert('删除失败，请重试')
        }
      }
    },
    async handleSubmit() {
      if (!validateRequired(this.formData.name)) {
        this.$alert('请输入姓名')
        return
      }

      if (!validateRequired(this.formData.idCard)) {
        this.$alert('请输入证件号码')
        return
      }

      if (!validateIdCard(this.formData.idCard)) {
        this.$alert('证件号码格式不正确')
        return
      }

      if (!validateRequired(this.formData.phone)) {
        this.$alert('请输入手机号')
        return
      }

      if (!validatePhone(this.formData.phone)) {
        this.$alert('手机号格式不正确')
        return
      }

      this.loading = true
      try {
        const wasEditing = !!this.editingId
        const data = {
          name: this.formData.name,
          idCard: this.formData.idCard,
          phone: this.formData.phone
        }

        if (this.editingId) {
          data.id = this.editingId
          await updateTraveler(data)
          this.$alert('更新成功')
        } else {
          const beforeIds = new Set(this.travelers.map(item => Number(item.id)))
          const result = await addTraveler(data)
          this.$alert('添加成功')
          await this.loadTravelers()
          if (this.selectionMode) {
            const returned = result && result.data
            const returnedId = Number(returned && typeof returned === 'object' ? returned.id : returned)
            const addedTraveler = this.travelers.find(item => Number(item.id) === returnedId)
              || this.travelers.find(item => !beforeIds.has(Number(item.id)) && item.name === data.name && item.idCard === data.idCard)
            if (addedTraveler && !this.isSelected(addedTraveler.id) && this.selectedIds.length < this.requiredCount) {
              this.selectedIds.push(Number(addedTraveler.id))
            }
          }
        }

        this.showForm = false
        this.resetForm()
        if (wasEditing) await this.loadTravelers()
      } catch (error) {
        console.error('操作失败:', error)
        this.$alert('操作失败，请重试')
      } finally {
        this.loading = false
      }
    },
    resetForm() {
      this.editingId = null
      this.formData = {
        name: '',
        idType: 0,
        idCard: '',
        phone: ''
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.traveler-list-page {
  width: 100%;
  min-height: 100vh;
  background: #f9fafb;
  padding-bottom: 80px;
}

.selection-header {
  position: sticky;
  top: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 14px;
  border-bottom: 1px solid #e5e7eb;
  background: #fff;
}

.selection-back {
  width: 72px;
  padding: 0;
  border: 0;
  color: #ff720d;
  background: transparent;
  font-size: 15px;
  text-align: left;
  cursor: pointer;
}

.selection-title { color: #111827; font-size: 16px; font-weight: 600; }
.selection-placeholder { width: 72px; }

.selection-tip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 13px 14px;
  color: #4b5563;
  background: #fff8f1;
  font-size: 13px;
}

.selection-count { color: #ff720d; font-weight: 600; }

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.5);
  z-index: 100;
  display: flex;
  align-items: flex-end;
}

.traveler-list-container {
  padding: 12px;
}

.travelers-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.traveler-item {
  background: #fff;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border: 1px solid #e5e7eb;

  &--selectable { cursor: pointer; }
  &.selected { border-color: #ff720d; background: #fff8f1; }
}

.traveler-check {
  display: flex;
  flex: 0 0 22px;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  margin-right: 10px;
  border: 2px solid #d1d5db;
  border-radius: 50%;
  color: #fff;
  background: #fff;
  font-size: 13px;
  font-weight: 700;

  .selected & { border-color: #ff720d; background: #ff720d; }
}

.traveler-info {
  flex: 1;
}

.traveler-name {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
}

.traveler-detail {
  font-size: 12px;
  color: #6b7280;
}

.traveler-actions {
  display: flex;
  gap: 8px;
}

.action-icon {
  width: 32px;
  height: 32px;
  border: none;
  background: #f3f4f6;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:active {
    opacity: 0.8;
  }
}

.edit-btn {
  color: #ff720d;
}

.delete-btn {
  color: #ef4444;
}

.form-container {
  width: 100%;
  max-width: 560px;
  box-sizing: border-box;
  background: #fff;
  border-radius: 16px 16px 0 0;
  padding: 16px;
  max-height: 80vh;
  overflow-y: auto;
}

.form-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.form-title {
  font-size: 16px;
  font-weight: 600;
  color: #111827;
}

.form-close {
  font-size: 20px;
  color: #6b7280;
  cursor: pointer;
}

.form-content {
  margin-bottom: 16px;
}

.form-group {
  margin-bottom: 16px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 8px;
}

.form-input {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  color: #111827;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #ff720d;
    box-shadow: 0 0 0 3px rgba(255, 114, 13, 0.12);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.form-actions {
  display: flex;
  gap: 12px;
  margin-top: 20px;
}

.form-btn {
  flex: 1;
  padding: 12px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:active {
    opacity: 0.8;
  }
}

.cancel-btn {
  background: #f3f4f6;
  color: #111827;
}

.submit-btn {
  background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  color: #fff;
}

.traveler-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  z-index: 10;
}

.traveler-footer--select {
  display: flex;
  gap: 10px;
  padding-bottom: max(12px, env(safe-area-inset-bottom));
}

.add-btn {
  width: 100%;
  padding: 12px;
  background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:active {
    opacity: 0.8;
  }
}

.traveler-footer--select .add-btn {
  flex: 1;
  width: auto;
  padding: 11px 8px;
  color: #ff720d;
  background: #fff0e3;
}

.confirm-selection-btn {
  flex: 1.45;
  min-width: 0;
  padding: 11px 8px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: linear-gradient(90deg, #ffa43a 0%, #ff720d 100%);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
}
</style>
